import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';
import {
  InviteMemberPayload,
  SeatKind,
  TeamMembersParams,
  TellentMember,
  TellentMemberResponse,
  TellentMemberRow,
} from '@tellent/types';

import {
  createEntityCollectionStore,
  memoizeSelector,
  MetaScopeOptions,
  ngrxEvent,
} from '@recruitee/ngrx';

import { resolveMemberSeat, resolveMemberStatus } from '../helpers';
import { EntityStoreKey } from '../types';
import { TellentAppsSelectors } from './tellent-apps.store';

const idSelector = ({ guid }: { guid: string }) => guid;

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TellentMember>()(
  EntityStoreKey.TellentMembers,
  {
    customActions: adapter => ({
      inviteMember: (state, payload: InviteMemberPayload) => adapter.markAsPending(state),
      inviteMemberSuccess: (state, payload: TellentMember[]) => adapter.markAsPending(state),
      inviteMemberFail: ngrxEvent<HttpErrorResponse>(),

      fetchPaginatedCollection: (state, payload: { params: TeamMembersParams }) =>
        adapter.markAsPending(state),

      deactivateMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
      deactivateMemberSuccess: (state, payload: TellentMemberResponse) =>
        adapter.markAsLoaded(state),
      deactivateMemberFailed: ngrxEvent<HttpErrorResponse>(),

      reactivateMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
      reactivateMemberSuccess: (state, payload: TellentMemberResponse) =>
        adapter.markAsLoaded(state),
      reactivateMemberFailed: ngrxEvent<HttpErrorResponse>(),

      resendInvitation: (state, payload: { id: number }) => adapter.markAsPending(state),
      resendInvitationSuccess: (state, payload: { id: number }) => adapter.markAsLoaded(state),
      resendInvitationFailed: ngrxEvent<HttpErrorResponse>(),

      sendInvitation: (state, payload: { guid: string }) => adapter.markAsPending(state),
      sendInvitationSuccess: (state, payload: { guid: string }) => adapter.markAsLoaded(state),
      sendInvitationFailed: ngrxEvent<HttpErrorResponse>(),
    }),
    idSelector,
  },
);

const teamMembersRowSelector = memoizeSelector((props: MetaScopeOptions) =>
  createSelector(
    selectors.entitiesWithPagination({ scope: props.scope }),
    TellentAppsSelectors.entities(),
    ({ rows }, apps): TellentMemberRow[] =>
      rows.map(member => ({
        ...member,
        id: member.guid, // id is required for the data-table
        status: resolveMemberStatus(member),
        recruitment: resolveMemberSeat(member, apps, SeatKind.Recruitment),
        coreHr: resolveMemberSeat(member, apps, SeatKind.CoreHR),
      })),
  ),
);

const TellentMembersSelectors = {
  ...selectors,
  teamMembersRowSelector,
};

export {
  actions as TellentMembersActions,
  TellentMembersSelectors,
  reducer as TellentMembersReducer,
  namespace as TellentMembersNamespace,
};
